import axios from "axios";
import axiosInstance from "../utils/axiosInstance";
import { Itinerary } from "../interfaces/itinerary.interface";
import { SeriesSearchParams } from "../interfaces/search-params.interface";
import { removeNullProperties } from "../utils/helpers";
import { pricingCategory } from "../enums/pricing.enum";
import { Cookies } from 'js-cookie';

const currencyDisplay = (currency, value) => {
  if (currency === "MYR") {
    const formatter = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })
    return `RM ${formatter.format(value)}`
  }

  const formatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: currency})
  return formatter.format(value)
}

class SeriesApi {
  getItineraries(searchParamsObj: SeriesSearchParams): Promise<{ itineraries: Itinerary[], itinerary_b2c_v2: boolean, total: number }> {
    const searchParamsQuery = this.createSearchParams(searchParamsObj);
    const apiUrl = `/api/v1/series`;

    return axios.get(apiUrl, {
      params: searchParamsQuery
    })
    .then(response => {
      const itineraries:Itinerary[] = response.data.itineraries.map(itineraryData => this.mapItinerary(itineraryData));
      const itinerary_b2c_v2 = response.data.itinerary_b2c_v2 ?? false;
      const total = response.data.itineraries[0]?.total_itineraries ?? itineraries.length;
      return Promise.resolve({ itineraries, itinerary_b2c_v2, total });
    });
  }

  getItinerariesV2(searchParamsObj: SeriesSearchParams): Promise<{ itineraries: Itinerary[], itinerary_b2c_v2: boolean, total: number }> {
    const searchParamsQuery = this.createSearchParams(searchParamsObj);
    const apiUrl = `/b2c2b/api/v1/series`;

    return axiosInstance.get(apiUrl, {
      params: searchParamsQuery
    })
    .then(response => {
      const itineraries:Itinerary[] = response.data.itineraries.map(itineraryData => this.mapItinerary(itineraryData));
      const itinerary_b2c_v2 = response.data.itinerary_b2c_v2 ?? false;
      const total = response.data.itineraries[0]?.total_itineraries ?? itineraries.length;
      return Promise.resolve({ itineraries, itinerary_b2c_v2, total });
    });
  }

  getItinerary(id, type="series", date?, businessType="b2b"): Promise<Itinerary>{
    const queryParams = new URLSearchParams({ business_type: businessType });

    if (type) {
      queryParams.set("type", type);
    }

    if (date) {
      queryParams.set("depart_date", date);
    }

    const apiUrl = `/api/v1/series/${id}?${queryParams.toString()}`;

    return axios.get(apiUrl)
    .then(response => {
      const itinerary = this.mapItinerary(response.data);
      return Promise.resolve(itinerary);
    });
  }

  getItineraryV2(id, type="series", date?, businessType="b2c2b", ref?: string | null): Promise<Itinerary>{
    const queryParams = new URLSearchParams({ business_type: businessType });

    if (type) {
      queryParams.set("type", type);
    }

    if (date) {
      queryParams.set("depart_date", date);
    }

    if (ref) {
      queryParams.set("ref", ref);
    }

    const apiUrl = `/b2c2b/api/v1/series/${id}?${queryParams.toString()}`;

    return axiosInstance.get(apiUrl)
    .then(response => {
      const itinerary = this.mapItinerary(response.data);
      return Promise.resolve(itinerary);
    });
  }

  getCabinNavigation(id, businessType="b2b", tourId?: number | string): Promise<any>{
    const queryParams = new URLSearchParams({ business_type: businessType });
    if (tourId) queryParams.set("tour_id", String(tourId));
    const apiUrl = `/api/v1/series/${id}/cruise?${queryParams.toString()}`;

    return axios.get(apiUrl)
    .then(response => Promise.resolve(this.normalizeCabinNavigation(response.data.cabin_navigation)));
  }

  private normalizeCabinNavigation(cabinNavigation: any) {
    if (!cabinNavigation) return cabinNavigation;

    cabinNavigation.departures = (cabinNavigation.departures || []).map((departure: any) => ({
      ...departure,
      from_price: Number(departure.from_price),
      room_types: (departure.room_types || []).map((roomType: any) => ({
        ...roomType,
        from_price: Number(roomType.from_price),
        cabins: (roomType.cabins || []).map((cabin: any) => ({
          ...cabin,
          from_price: Number(cabin.from_price),
          detail: cabin.detail ? this.normalizeTour(cabin.detail, null) : cabin.detail,
        })),
      })),
    }));

    return cabinNavigation;
  }

  private normalizeTour(tour: any, priceCurrency: string | null) {
    if (priceCurrency) tour.price = currencyDisplay(priceCurrency, tour.price);
    tour.highlight = tour.highlight_customer;
    tour.insurance_rebate = Number(tour.insurance_rebate);
    tour.visa_fee = Number(tour.visa_fee);
    tour.prices = tour.prices?.map((p: any) => ({
      ...p,
      adult:                 Number(p.adult),
      child_twin:            Number(p.child_twin),
      child_with_bed:        Number(p.child_with_bed),
      child_no_bed:          Number(p.child_no_bed),
      single_supplement:     Number(p.single_supplement),
      infant:                Number(p.infant),
      dta_adult:             Number(p.dta_adult),
      dta_child_twin:        Number(p.dta_child_twin),
      dta_child_with_bed:    Number(p.dta_child_with_bed),
      dta_child_no_bed:      Number(p.dta_child_no_bed),
      dta_single_supplement: Number(p.dta_single_supplement),
      dta_infant:            Number(p.dta_infant),
    }));
    return { ...tour, room_types: pricingCategory(tour.room_types) };
  }

  private mapItinerary(itineraryData):Itinerary {
    const itinerary:Itinerary = {
      id: itineraryData.id,
      description: itineraryData.description,
      caption: itineraryData.caption,
      otherCaption: itineraryData.otherCaption,
      code: itineraryData.code,
      category: itineraryData.category,
      category_star: itineraryData.category_star,
      country: itineraryData.country,
      price: currencyDisplay(itineraryData.price_currency, itineraryData.price),
      priceValue: Number(itineraryData.price),
      saveAmount: Number(itineraryData.save_amount) || 0,
      normalPrice:
        Number(itineraryData.save_amount) > 0
          ? currencyDisplay(itineraryData.price_currency, Number(itineraryData.price) + Number(itineraryData.save_amount))
          : undefined,
      normalPriceValue:
        Number(itineraryData.save_amount) > 0
          ? Number(itineraryData.price) + Number(itineraryData.save_amount)
          : undefined,
      saveDisplay:
        Number(itineraryData.save_amount) > 0
          ? currencyDisplay(itineraryData.price_currency, Number(itineraryData.save_amount))
          : undefined,
      images: itineraryData.images,
      coverImage: itineraryData.images[0],
      priceCurrency: itineraryData.price_currency,
      departureDate: itineraryData.departure_date,
      guranteedDepartureDates: itineraryData.guranteed_departure_dates,
      almostGuaranteedDepartureDates: itineraryData.almost_guaranteed_departure_dates,
      departureLocations: itineraryData.departure_locations,
      totalItineraries: itineraryData.total_itineraries,
      fileUrl: itineraryData.file_url,
      fileUrlCopy: itineraryData.file_copy_url,
      fileCNUrl: itineraryData.cn_file_url,
      fileCNUrlCopy: itineraryData.file_cn_copy_url,
      insurancePolicyUrl: itineraryData.insurance_policy_url,
      insuranceBenefitUrl: itineraryData.insurance_benefit_url,
      isNew: itineraryData.is_new,

      autoCancel: itineraryData.auto_cancel,
      ccRate: itineraryData.cc_rate,
      includings: itineraryData.includings,
      maxBookingSeats: itineraryData.max_booking_seats,
      remark: itineraryData.remark,
      tourType: itineraryData.tour_type,
      tours: itineraryData.tours?.map(tour => this.normalizeTour(tour, itineraryData.price_currency)),
      b2c2b: itineraryData.has_b2c2b_fair,
      checkoutEnabled: itineraryData.b2c2b?.checkoutable,
      checkoutDisabledMessage: itineraryData.b2c2b?.checkout_disabled_message,
      agentChangeReasons: itineraryData.b2c2b?.change_reasons || {},
      v2: itineraryData.itinerary_b2c_v2,
      v3: itineraryData.itinerary_b2c_v3_mobile,
      cruiseCabinDisplay: itineraryData.cruise_cabin_display,
      nextApi: itineraryData.next_api,
      cabinNavigation: itineraryData.cabin_navigation,
      itinerary_plans: itineraryData.itinerary_plans,
      special_disclaimer: itineraryData.special_disclaimer,
      is_itinerary_plans_preview_mode: itineraryData.is_itinerary_plans_preview_mode,
      itinerary_plans_preview_status: itineraryData.itinerary_plans_preview_status,
      child_age_ranges: itineraryData.child_age_ranges && {
        child_twin: itineraryData.child_age_ranges.child_twin,
        child_with_bed: itineraryData.child_age_ranges.child_with_extra_bed,
        child_no_bed: itineraryData.child_age_ranges.child_no_bed,
      },
    }

    return itinerary;
  }

  private createSearchParams(searchParamsObj: SeriesSearchParams){
    const {location_id, ...params} = searchParamsObj;
    return removeNullProperties(params);
  }
}

export default SeriesApi;
